import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Play } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { reciterAPI, recitationAPI } from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ReciterList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [reciters, setReciters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReciters();
  }, []);

  const loadReciters = async () => {
    try {
      const response = await reciterAPI.getAll();
      const recitersData = response.data || [];
      
      // Load recitation count and total duration for each reciter
      const recitersWithStats = await Promise.all(
        recitersData.map(async (reciter) => {
          try {
            const recitationsRes = await recitationAPI.getByReciter(reciter.id);
            const recitationsList = recitationsRes.data || [];
            
            // Calculate total duration
            const totalSeconds = recitationsList.reduce((sum, rec) => {
              if (rec.duration) {
                const parts = rec.duration.split(':');
                let hours = 0, minutes = 0, seconds = 0;
                
                if (parts.length === 3) {
                  // Format: HH:MM:SS
                  hours = parseInt(parts[0]) || 0;
                  minutes = parseInt(parts[1]) || 0;
                  seconds = parseInt(parts[2]) || 0;
                } else if (parts.length === 2) {
                  // Format: MM:SS
                  minutes = parseInt(parts[0]) || 0;
                  seconds = parseInt(parts[1]) || 0;
                }
                
                return sum + (hours * 3600) + (minutes * 60) + seconds;
              }
              return sum;
            }, 0);
            
            const totalMinutes = Math.floor(totalSeconds / 60);
            const totalHours = Math.floor(totalMinutes / 60);
            const remainingMinutes = totalMinutes % 60;
            const totalDuration = totalHours > 0 
              ? `${totalHours} saat ${remainingMinutes} dk`
              : `${totalMinutes} dk`;
            
            return {
              ...reciter,
              recitationCount: recitationsList.length,
              totalDuration: totalDuration
            };
          } catch (error) {
            return { ...reciter, recitationCount: 0, totalDuration: '0 dk' };
          }
        })
      );
      
      setReciters(recitersWithStats);
    } catch (error) {
      console.error('Error loading reciters:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url.replace('/uploads/', '/api/files/')}`;
  };

  const filteredReciters = reciters.filter(reciter =>
    reciter.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reciter.nameArabic.includes(searchTerm) ||
    reciter.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleReciterClick = (reciter) => {
    // Use slug if available, otherwise use ID
    const identifier = reciter.slug || reciter.id;
    navigate(`/hafizlar/${identifier}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6F1] flex items-center justify-center">
        <p className="text-[#8B7355] text-lg">Yukleniyor...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6F1] py-4 sm:py-8 pb-20 md:pb-8">
      <div className="max-w-5xl mx-auto px-3 sm:px-6">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#5C4033] mb-2">Hafızlar</h1>
          <p className="text-sm sm:text-base text-[#8B7355]">Kuran-ı Kerim'i güzel sesle okuyan hafızları dinleyin</p>
        </div>

        {/* Search */}
        <div className="mb-4 sm:mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8B7355] h-4 w-4 sm:h-5 sm:w-5" />
            <Input
              type="text"
              placeholder="Hafız ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 sm:pl-10 bg-white border-[#D5C4A1] text-[#5C4033] placeholder:text-[#B8A894] text-sm sm:text-base"
            />
          </div>
        </div>

        {/* Reciter Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {filteredReciters.map((reciter) => (
            <Card
              key={reciter.id}
              className="bg-white border-[#E8DCC8] hover:border-[#D5C4A1] hover:shadow-lg cursor-pointer overflow-hidden group"
            >
              <div className="p-3 sm:p-6">
                <div className="flex flex-col items-center text-center">
                  {/* Avatar with Image - Küçültüldü - Tıklanabilir */}
                  <div 
                    className="relative h-16 w-16 sm:h-20 sm:w-20 mb-3 sm:mb-4 cursor-pointer"
                    onClick={() => handleReciterClick(reciter)}
                  >
                    <img 
                      src={getImageUrl(reciter.image)} 
                      alt={reciter.name}
                      className="h-full w-full rounded-full object-cover border-4 border-[#E8DCC8] group-hover:border-[#D5C4A1] hover:scale-105 transition-transform"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    <div 
                      className="h-full w-full rounded-full bg-[#F5EFE7] text-[#5C4033] text-2xl flex items-center justify-center border-4 border-[#E8DCC8] group-hover:border-[#D5C4A1]"
                      style={{ display: 'none' }}
                    >
                      {reciter.name.charAt(0)}
                    </div>
                  </div>

                  {/* Name */}
                  <h3 className="text-sm sm:text-lg font-bold text-[#5C4033] mb-1 line-clamp-1">
                    {reciter.name}
                  </h3>

                  {/* Country with Flag - mobilde bayrak küçük */}
                  <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-2">
                    {reciter.flagUrl && (
                      <img 
                        src={reciter.flagUrl.startsWith('http') ? reciter.flagUrl : `${process.env.REACT_APP_BACKEND_URL}${reciter.flagUrl.replace('/uploads/', '/api/files/')}`} 
                        alt={`${reciter.country} Bayrağı`} 
                        className="w-6 h-4 sm:w-8 sm:h-6 object-cover rounded"
                      />
                    )}
                    <p className="text-xs sm:text-sm text-[#8B7355]">
                      {reciter.country}
                    </p>
                  </div>

                  {/* Recitation Count & Total Duration */}
                  <div className="text-xs sm:text-sm text-[#8B7355] mb-3 sm:mb-4 space-y-0.5 sm:space-y-1">
                    <p>
                      <span className="font-semibold">{reciter.recitationCount || 0}</span> ses kayıtı
                    </p>
                    <p>
                      Toplam: <span className="font-semibold">{reciter.totalDuration || '0 dk'}</span>
                    </p>
                  </div>

                  {/* Play Button */}
                  <Button
                    onClick={() => handleReciterClick(reciter)}
                    size="sm"
                    className="bg-[#8B7355] hover:bg-[#6B5744] text-white w-full text-xs sm:text-sm"
                  >
                    <Play className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Sure Listesini Gör</span>
                    <span className="inline sm:hidden">Gör</span>
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* No Results */}
        {filteredReciters.length === 0 && (
          <div className="text-center py-12">
            <p className="text-[#8B7355]">Aradığınız hafız bulunamadı.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReciterList;